import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  List,
  type MediumIcon,
  Row,
  Title,
} from '@factorialco/deprecated-design-system'
import { Switch } from '@factorialco/f0-react/dist/experimental'
import {
  HideCompensationsCycleDocument,
  SettingCyclesDocument,
  UnhideCompensationsCycleDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import HideCycleConfirmationModal from './HideCycleConfirmationModal'

export interface ModuleConfig {
  key: string
  titleKey: string
  illustration: MediumIcon
  navigationPath: string
}

const Cycles = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { data, loading, error } = useQuery(SettingCyclesDocument)
  const cycles = data?.compensations?.cyclesConnection?.nodes ?? []

  const [pendingCycle, setPendingCycle] = useState<{
    id: number
    label: string
    currency: string
    isHiding: boolean
  } | null>(null)

  const [hideCycle] = useMutation(HideCompensationsCycleDocument, {
    extractorFn: (payload) =>
      payload.compensationsMutations?.hideCompensationsCycle ?? { errors: [] },
    refetchQueries: [SettingCyclesDocument],
  })

  const [unhideCycle] = useMutation(UnhideCompensationsCycleDocument, {
    extractorFn: (payload) =>
      payload.compensationsMutations?.unhideCompensationsCycle ?? { errors: [] },
    refetchQueries: [SettingCyclesDocument],
  })

  const handleToggle = (
    cycleId: number,
    cycleLabel: string,
    cycleCurrency: string,
    isCurrentlyVisible: boolean
  ) => {
    const newPendingCycle = {
      id: cycleId,
      label: cycleLabel,
      currency: cycleCurrency,
      isHiding: isCurrentlyVisible,
    }
    setPendingCycle(newPendingCycle)
  }

  const handleConfirm = async () => {
    if (!pendingCycle) return

    if (pendingCycle.isHiding) {
      const result = await hideCycle({ variables: { id: pendingCycle.id } })
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.save_error'),
        })
      }
    } else {
      const result = await unhideCycle({ variables: { id: pendingCycle.id } })
      const handled = handleResult(result, () => undefined)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('common.save_error'),
        })
      }
    }

    setPendingCycle(null)
  }

  const handleCloseModal = () => {
    setPendingCycle(null)
  }

  if (loading) {
    return <Loading />
  }

  if (error) {
    throw new Error('Error fetching data')
  }

  return (
    <Box flexDirection="row" alignItems="center" paddingX="s64" paddingY="s32" width="full">
      <Row>
        <Row.Col halfWidth>
          <Box paddingRight="s64">
            <Title
              variant="500"
              title={i18n.t('compensations.settings.cycles.title')}
              description={i18n.t('compensations.settings.cycles.description')}
              illustration={'calendar-clock'}
            />
          </Box>
        </Row.Col>
        <Row.Col halfWidth>
          <List>
            {cycles.map((cycle) => {
              const isVisible = !cycle.hiddenAt

              return (
                <List.Item key={cycle.id}>
                  <Box flexDirection="column">
                    <List.Title>{cycle.countryLabel}</List.Title>
                    <List.Description>{cycle.legalEntitiesLabel}</List.Description>
                  </Box>
                  <List.ActionItem>
                    <Box flexDirection="row" alignItems="center" gap="s16">
                      <Switch
                        checked={isVisible}
                        onCheckedChange={() =>
                          handleToggle(
                            cycle.id,
                            cycle.countryLabel || '',
                            cycle.currency || '',
                            isVisible
                          )
                        }
                      />
                      <Button
                        type="secondary"
                        iconName="arrow-right"
                        onClick={() =>
                          navigate(
                            navigation.settings.compensations.cycles.id.fullPath({ id: cycle.id })
                          )
                        }
                      ></Button>
                    </Box>
                  </List.ActionItem>
                </List.Item>
              )
            })}
          </List>
        </Row.Col>
      </Row>
      {pendingCycle && (
        <HideCycleConfirmationModal
          isHiding={pendingCycle.isHiding}
          cycleLabel={pendingCycle.label}
          cycleCurrency={pendingCycle.currency}
          onConfirm={handleConfirm}
          onClose={handleCloseModal}
        />
      )}
    </Box>
  )
}

export default Cycles
